/**
 * KAN-862: панель канбан-доски CRM.
 * На телефоне элементы в одну строку с горизонтальной прокруткой внутри панели
 * (страница вбок не прокручивается), на широком экране с переносом.
 * KAN-865: переключатель "Требуют внимания (N)" (только клиенты внимания).
 * KAN-882: вид доски вместо группировки и исхода: кабинет - сегменты
 * "Вид доски" (Работа / Менеджеры / Теплота / Воронка), телефон - компактный
 * список "Вид". "Требуют внимания (N)" только в видах managers и funnel.
 * KAN-883: "Активность" - период последней активности (CrmPeriodControl:
 * Сегодня / Вчера / 7 дней / Всё время и календарь) вместо active_days.
 * KAN-905: рядом с периодом переключатель "Только новые лиды" (состояние в
 * useNewLeadsOnly, общий с таблицей); при "Всё время" неактивен.
 */
import type { CrmRange } from '@/lib/crmTable';
import type { PeriodPreset } from '@/lib/periodPresets';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CrmPeriodControl } from '@/components/crm/CrmPeriodControl';
import { NewLeadsToggle } from '@/components/crm/NewLeadsToggle';
import type { NewLeadsOnlyState } from '@/hooks/useNewLeadsOnly';
import { CRM_BOARD_PERIOD_CHOICES } from '@/lib/crmBoardActivity';
import { SegmentedControl } from './SegmentedControl';
import { CRM_BOARD_VIEW_OPTIONS, boardHasAttentionFilter, parseBoardView } from '@/lib/crmPipeline';
import type { CrmBoardView } from '@/types/crmPipeline';

export interface BoardToolbarProps {
  board: CrmBoardView;
  onBoardChange: (value: CrmBoardView) => void;
  /** Кабинет: сегменты вида; телефон: выпадающий список. */
  variant: 'desktop' | 'mobile';
  activity: CrmRange | null;
  onActivityChange: (value: CrmRange | null) => void;
  /** KAN-910: быстрый период именем (?activity=today); без него - датами в onActivityChange. */
  onActivityPreset?: (value: PeriodPreset) => void;
  /** KAN-865: показаны только требующие внимания. */
  attentionOnly?: boolean;
  /** KAN-865: переключить фильтр внимания; без обработчика переключателя нет. */
  onAttentionOnlyChange?: (next: boolean) => void;
  /** KAN-865: число требующих внимания (attention_count доски); нет: без числа. */
  attentionCount?: number | null;
  /** KAN-905: "Только новые лиды"; без состояния переключателя нет. */
  newLeads?: NewLeadsOnlyState;
}

function BoardViewSelect({ board, onChange }: { board: CrmBoardView; onChange: (value: CrmBoardView) => void }) {
  return (
    <select
      aria-label="Вид"
      data-testid="crm-board-view-select"
      value={board}
      onChange={(event) => onChange(parseBoardView(event.target.value))}
      className="h-8 shrink-0 rounded-md border border-gray-200 bg-white px-2 text-sm font-medium text-gray-800"
    >
      {CRM_BOARD_VIEW_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function BoardToolbar({
  board,
  onBoardChange,
  variant,
  activity,
  onActivityChange,
  onActivityPreset,
  attentionOnly = false,
  onAttentionOnlyChange,
  attentionCount,
  newLeads,
}: BoardToolbarProps): JSX.Element {
  const attentionFilter = !!onAttentionOnlyChange && boardHasAttentionFilter(board);
  return (
    <div data-testid="crm-board-toolbar" className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
      {/* KAN-963: вид и период вне прокручиваемого ряда: окно календаря не обрезается. */}
      {variant === 'mobile' ? (
        <BoardViewSelect board={board} onChange={onBoardChange} />
      ) : (
        <SegmentedControl
          label="Вид доски"
          options={CRM_BOARD_VIEW_OPTIONS}
          value={board}
          onChange={onBoardChange}
          testId="crm-board-view-switch"
        />
      )}
      <CrmPeriodControl
        label="Активность"
        choices={CRM_BOARD_PERIOD_CHOICES}
        range={activity}
        onChange={onActivityChange}
        onPreset={onActivityPreset}
        onAllTime={() => onActivityChange(null)}
        testId="crm-board-activity"
        showLabel
      />
      {(newLeads || attentionFilter) && (
        <div
          data-testid="crm-board-toolbar-scroll"
          className="-mx-1 flex min-w-0 max-w-full items-center gap-2 overflow-x-auto px-1 pb-1 md:flex-wrap md:overflow-visible"
        >
          {newLeads && <NewLeadsToggle state={newLeads} />}
          {attentionFilter && (
            <button
              type="button"
              data-testid="crm-board-attention-filter"
              aria-pressed={attentionOnly}
              onClick={() => onAttentionOnlyChange?.(!attentionOnly)}
              className={cn(
                'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-2.5 py-1 text-sm font-medium transition-colors',
                attentionOnly
                  ? 'border-rose-600 bg-rose-600 text-white'
                  : 'border-rose-200 bg-white text-rose-700 hover:bg-rose-50'
              )}
            >
              <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
              Требуют внимания
              {typeof attentionCount === 'number' && ` (${attentionCount})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
